import { RefObject, useState } from "react";
import { imageList } from "@/core/utils/imageList";
import { PartnerItem } from "@/features/partners/components/PartnerItem";
import { PartnerResponse } from "@/features/partners/model/types";
import { OrganizationSearchResult, ProposedPartner } from "../../model/types";

interface OrganizationChoiceStepProps {
  // Search
  searchQuery: string;
  searchResults: OrganizationSearchResult[];
  searchLoading: boolean;
  searchSectionRef: RefObject<HTMLDivElement>;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isFromSettings?: boolean;
  // Selection
  selectedOrganizations: OrganizationSearchResult[];
  onSelectOrganization: (org: OrganizationSearchResult) => void;
  onRemoveOrganization: (orgId: string) => void;

  // Proposed partners
  proposedPartners: ProposedPartner[];
  proposedPartnersLoading: boolean;
  convertToSearchResult: (partner: ProposedPartner) => OrganizationSearchResult;

  // Sector expansion
  expandedSectors: Set<string>;
  onToggleSectorExpansion: (partnerId: string) => void;
  submitPartnerOnly?: boolean;
  // Actions
  onCreateOrganization: () => void;
  onJoinSelectedOrganizations: () => void;
  onSkip: () => void;
}

export const OrganizationChoiceStep = ({
  searchQuery,
  searchResults,
  searchLoading,
  searchSectionRef,
  onSearchChange,
  selectedOrganizations,
  onSelectOrganization,
  onRemoveOrganization,
  proposedPartners,
  proposedPartnersLoading,
  convertToSearchResult,
  expandedSectors,
  onToggleSectorExpansion,
  onCreateOrganization,
  onJoinSelectedOrganizations,
  onSkip,
  isFromSettings,
  submitPartnerOnly,
}: OrganizationChoiceStepProps) => {
  const isOrgSelected = (orgId: string) =>
    selectedOrganizations.some((s) => s.id === orgId);

  const [hasSelectedOrg, setHasSelectedOrg] = useState(false);

  return (
    <div className="step-content step-5">
      <div className="step-header">
        <p className="step-subtitle">Create or join your organisation</p>
        <h2 className="step-title">
          Do you want to create your organisation profile?
        </h2>
      </div>

      <button
        type="button"
        className="submit-button create-org-button"
        onClick={onCreateOrganization}
      >
        Create organisation
      </button>

      {!isFromSettings && (
        <div className="or-divider">
          <span>or join an existing organisation</span>
        </div>
      )}

      {/* Search Section */}
      {!isFromSettings && (
        <div className="search-section" ref={searchSectionRef}>
          <div className="search-input-wrapper">
            <i className="pi pi-search"></i>
            <input
              type="text"
              placeholder="Search for an organisation"
              value={searchQuery}
              onChange={(e) => {
                onSearchChange(e);
                setHasSelectedOrg(false);
              }} // Reset selection state when search changes
              className="search-input"
            />
          </div>

          {searchLoading && <div className="search-loading">Searching...</div>}

          {searchResults.length > 0 && !hasSelectedOrg && (
            <div className="search-results">
              {searchResults.map((org) => {
                const isSelected = isOrgSelected(org.id);
                return (
                  <PartnerItem
                    key={org.id}
                    partner={org as unknown as PartnerResponse}
                    expandedSectors={expandedSectors}
                    onToggleSectorExpansion={onToggleSectorExpansion}
                    onClick={() => {
                      if (selectedOrganizations.length === 0) {
                        setHasSelectedOrg(true);
                        onSelectOrganization(org);
                      }
                    }}
                  />
                );
              })}
            </div>
          )}

          {searchQuery.length >= 3 &&
            !searchLoading &&
            searchResults.length === 0 && (
              <div className="no-results">No organizations found</div>
            )}
        </div>
      )}

      {/* Selected Organizations */}
      {selectedOrganizations.length > 0 && (
        <div className="selected-organizations-section">
          <h3 className="organizations-title">Selected</h3>
          <div className="organizations-list">
            {selectedOrganizations.map((org) => (
              <PartnerItem
                key={org.id}
                partner={org as unknown as PartnerResponse}
                expandedSectors={expandedSectors}
                onToggleSectorExpansion={onToggleSectorExpansion}
                onClick={() => onRemoveOrganization(org.id)}
                isSelected
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommended Organizations */}
      {proposedPartners && proposedPartners.length > 0 && (
        <div className="recommendations-section">
          <h3 className="recommendations-title">Recommended for you</h3>

          {proposedPartnersLoading && (
            <div className="organizations-loading">
              Loading recommendations...
            </div>
          )}

          <div className="organizations-list">
            {proposedPartners.map((org) => {
              const isSelected = isOrgSelected(org.id);
              return (
                <div
                  key={org.id}
                  className={`organization-card ${
                    isSelected ? "selected" : ""
                  }`}
                  onClick={() => {
                    if (selectedOrganizations.length === 0) {
                      onSelectOrganization(convertToSearchResult(org));
                    } else if (isSelected) {
                      onRemoveOrganization(org.id);
                    }
                  }}
                  style={
                    isSelected
                      ? { border: "2px solid #28a745", position: "relative" }
                      : { position: "relative" }
                  }
                >
                  <div className="org-logo">
                    <img
                      src={org.logo_uri || imageList.noimage}
                      alt={org.title}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = imageList.noimage;
                      }}
                    />
                  </div>
                  <div className="org-info">
                    <h4 className="org-name">{org.title}</h4>
                    <div className="org-details">
                      <span className="org-type">
                        <i className="pi pi-building"></i> {org.org_type}
                      </span>
                      <span className="org-location">
                        <i className="pi pi-map-marker"></i> {org.location}
                      </span>
                      <span className="org-year">
                        <i className="pi pi-calendar"></i> Since {org.since}
                      </span>
                      {org.size.length > 0 && (
                        <span className="org-size">
                          <i className="pi pi-users"></i> {org.size[0].name}
                        </span>
                      )}
                      <span className="org-programmes">
                        <i className="pi pi-briefcase"></i>{" "}
                        {org.programmes_count} programmes
                      </span>
                    </div>
                    <div className="org-tags">
                      {org.sectors.slice(0, 3).map((sector) => (
                        <span key={sector.id} className="org-tag">
                          {sector.name}
                        </span>
                      ))}
                      {org.sectors.length > 3 && (
                        <span className="more-tags">
                          +{org.sectors.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        background: "#28a745",
                        color: "white",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="pi pi-check"
                        style={{ fontSize: "12px" }}
                      ></i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Join Button */}
      {selectedOrganizations.length > 0 && (
        <button
          type="button"
          className="submit-button"
          onClick={onJoinSelectedOrganizations}
          style={{ marginTop: "2rem", marginBottom: "1rem" }}
        >
          Ask to join
          {/* Ask to join ({selectedOrganizations.length} organization 
          {selectedOrganizations.length > 1 ? "s" : ""})*/}
        </button>
      )}

      {!submitPartnerOnly && (
        <button type="button" className="skip-button" onClick={onSkip}>
          Skip this step
        </button>
      )}
    </div>
  );
};
